/**
 * Utility functions for CSV file handling and validation
 */

import Papa from "papaparse";

// Constants for validation
const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_CSV_EXTENSIONS = ['.csv'];
const VALID_CSV_MIME_TYPES = ['text/csv', 'application/csv', 'text/plain'];

/**
 * Validates file type to ensure it's a CSV file
 * @param file - The file to validate
 * @returns string error message or null if valid
 */
const validateFileType = (file: File): string | null => {
  const fileName = file.name.toLowerCase();
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
  
  // Check file extension
  if (!VALID_CSV_EXTENSIONS.includes(fileExtension)) {
    return `Please upload a CSV file. The selected file appears to be a ${fileExtension} file, but we need a .csv file for plot map data.`;
  }
  
  // Check MIME type (note: some systems don't set CSV MIME type correctly, so we're lenient)
  if (file.type && !VALID_CSV_MIME_TYPES.includes(file.type)) {
    return `The selected file doesn't appear to be a CSV file. Please ensure you're uploading a .csv file containing your plot data.`;
  }
  
  return null;
};

/**
 * Validates file size to ensure it's reasonable for processing
 * @param file - The file to validate
 * @returns string error message or null if valid
 */
const validateFileSize = (file: File): string | null => {
  if (file.size === 0) {
    return "The selected file is empty. Please upload a CSV file that contains your plot data.";
  }
  
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return `The selected file is ${fileSizeMB}MB, which is too large. Please upload a CSV file smaller than ${MAX_FILE_SIZE_MB}MB. Consider removing unnecessary columns or splitting large datasets.`;
  }
  
  return null;
};

/**
 * Generates plot map specific warnings based on column analysis
 * @param columnAnalysis - The column analysis results
 * @param headers - Array of column headers
 * @returns Array of warning messages
 */
const generatePlotMapWarnings = (columnAnalysis: CSVColumnAnalysis, headers: string[]): string[] => {
  const warnings: string[] = [];
  
  // Check if there are no unique value columns (no suitable plot ID columns)
  if (columnAnalysis.uniqueValueColumns.length === 0) {
    warnings.push("No columns with unique values were found. You'll need at least one column with unique identifiers for each plot (like plot_id or sample_id) to create a proper plot map.");
  }
  
  // Check if there are no integer columns (no suitable row/column mapping)
  if (columnAnalysis.integerColumns.length === 0) {
    warnings.push("No columns with numeric values were found. If you want to map plots to a grid layout, consider adding columns with row and column numbers.");
  }
  
  // Check if there are integer columns but no valid pairs for row/column mapping
  if (columnAnalysis.integerColumns.length > 0 && Object.keys(columnAnalysis.rowColumnCandidates).length === 0) {
    warnings.push("While numeric columns were found, they don't form unique combinations suitable for grid mapping. Each row/column pair should uniquely identify a plot.");
  }
  
  
  // Warning if CSV has very few rows
  if (headers.length < 2) {
    warnings.push("Your CSV only has one column. Consider adding more columns with plot information like treatment, variety, or location data.");
  }
  
  return warnings;
};

export interface CSVColumnAnalysis {
  uniqueValueColumns: string[];
  integerColumns: string[];
  rowColumnCandidates: Record<string, string[]>;
  /** Columns whose every data row is blank (candidates to drop on upload). */
  emptyColumns: string[];
}

export interface CSVValidationResult {
  isValid: boolean;
  error?: string;
  rowCount?: number;
  columnCount?: number;
  headers?: string[];
  parsedData?: Record<string, string>[];
  columnAnalysis?: CSVColumnAnalysis;
  warnings?: string[];
}

export interface CSVParseResult {
  headers: string[];
  data: Record<string, string>[];
  headerIndexMapping: number[];
}

/**
 * Validates the shape and structure of a CSV file
 * @param file - The CSV file to validate
 * @returns Promise<boolean> - True if the CSV has a valid structure
 */
export const validateCSVShape = async (file: File): Promise<boolean> => {
  try {
    const result = await validateCSVShapeDetailed(file);
    return result.isValid;
  } catch (error) {
    console.error("Error validating CSV shape:", error);
    return false;
  }
};

/**
 * Validates the shape and structure of a CSV file with detailed results
 * @param file - The CSV file to validate
 * @returns Promise<CSVValidationResult> - Detailed validation results
 */
export const validateCSVShapeDetailed = async (file: File): Promise<CSVValidationResult> => {
  return new Promise((resolve) => {
    // Pre-parse validation
    const fileTypeError = validateFileType(file);
    if (fileTypeError) {
      resolve({
        isValid: false,
        error: fileTypeError,
      });
      return;
    }
    
    const fileSizeError = validateFileSize(file);
    if (fileSizeError) {
      resolve({
        isValid: false,
        error: fileSizeError,
      });
      return;
    }
    Papa.parse(file, {
      header: false, // Parse without headers first to validate structure
      skipEmptyLines: false, // Don't skip empty lines during validation
      complete: (results) => {
        try {
          // PapaParse emits a benign "UndetectableDelimiter" notice for
          // single-column files — there is no delimiter character (comma, tab,
          // etc.) to auto-detect, so it defaults to comma and still parses each
          // line correctly. A CSV that is just a column of plot IDs is a valid
          // single-column plot map, so don't treat this notice as fatal.
          const fatalErrors = results.errors.filter(
            (e) => e.code !== "UndetectableDelimiter"
          );
          if (fatalErrors.length > 0) {
            const errorMessages = fatalErrors.map((e) => e.message).join(", ");
            resolve({
              isValid: false,
              error: `Unable to read your CSV file. Please check that it's properly formatted. Details: ${errorMessages}`,
            });
            return;
          }

          const rows = results.data as string[][];

          // CSV shape validation
          if (!rows || rows.length === 0) {
            resolve({
              isValid: false,
              error: "Your CSV file appears to be empty. Please upload a CSV file that contains plot data with headers and at least one row of data.",
            });
            return;
          }

          // Ensure header line exists and is non-empty
          const headerRow = rows[0];
          if (!headerRow || headerRow.length === 0) {
            resolve({
              isValid: false,
              error: "Your CSV file needs column headers in the first row. Please add headers like 'plot_id', 'row', 'column', etc. to identify your data columns.",
            });
            return;
          }

          // Split headers by comma and trim whitespace
          const rawHeaders = headerRow.map((header) => header.trim());

          // Build headers from non-empty header names only; keep mapping of which indices were kept
          const headers: string[] = [];
          const headerIndexMapping: number[] = [];

          rawHeaders.forEach((header, index) => {
            if (header !== "") {
              headers.push(header);
              headerIndexMapping.push(index);
            }
          });

          if (headers.length === 0) {
            resolve({
              isValid: false,
              error: "Your CSV file's header row contains only empty columns. Please provide meaningful column names like 'plot_id', 'treatment', 'variety', etc.",
            });
            return;
          }

          // Ensure at least one non-empty data row
          let hasDataRows = false;
          const dataRows: string[][] = [];

          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (row && row.length > 0) {
              // Check if row has any non-empty values
              const hasNonEmptyValues = row.some((cell) => cell && cell.trim() !== "");
              if (hasNonEmptyValues) {
                hasDataRows = true;
                dataRows.push(row);
              }
            }
          }

          if (!hasDataRows) {
            resolve({
              isValid: false,
              error: "Your CSV file only contains headers but no plot data. Please add at least one row of data below the header row.",
            });
            return;
          }

          // For each non-empty row: if there are more columns than headers and any "extra" is non-empty, fail
          for (let i = 0; i < dataRows.length; i++) {
            const row = dataRows[i];
            if (row.length > rawHeaders.length) {
              // Check if any extra columns contain non-empty data
              for (let j = rawHeaders.length; j < row.length; j++) {
                if (row[j] && row[j].trim() !== "") {
                  resolve({
                    isValid: false,
                    error: `Row ${i + 2} has extra data in column ${j + 1} that doesn't match any header. Please ensure all data rows have the same number of columns as your headers, or remove the extra data.`,
                  });
                  return;
                }
              }
            }
          }

          // Parse the data according to the rules
          const parsedData = dataRows.map((row, rowIndex) => {
            const rowData: Record<string, string> = {};

            // Keep values only at kept header indices; pad with empty strings to the header count
            for (let i = 0; i < headers.length; i++) {
              const originalIndex = headerIndexMapping[i];
              const value = originalIndex < row.length ? row[originalIndex] : "";
              rowData[headers[i]] = value ? value.trim() : "";
            }

            return rowData;
          });

          // Perform column analysis
          const columnAnalysis = analyzeCSVColumns(parsedData, headers);
          
          // Generate plot map specific warnings
          const warnings = generatePlotMapWarnings(columnAnalysis, headers);

          resolve({
            isValid: true,
            rowCount: parsedData.length,
            columnCount: headers.length,
            headers: headers,
            parsedData: parsedData,
            columnAnalysis: columnAnalysis,
            warnings: warnings.length > 0 ? warnings : undefined,
          });
        } catch (error) {
          resolve({
            isValid: false,
            error: `An unexpected error occurred while processing your CSV file. Please check that it's a valid CSV format and try again. Technical details: ${error instanceof Error ? error.message : "Unknown error"}`,
          });
        }
      },
      error: (error) => {
        resolve({
          isValid: false,
          error: `Unable to read your CSV file. This might be due to file corruption or an unsupported format. Please ensure you're uploading a valid CSV file. Technical details: ${error.message}`,
        });
      },
    });
  });
};

/**
 * Parses a CSV file and returns the data as an array of objects
 * @param file - The CSV file to parse
 * @returns Promise<Record<string, string>[]> - Array of objects representing CSV rows
 */
export const parseCSVFile = async (file: File): Promise<Record<string, string>[]> => {
  const result = await validateCSVShapeDetailed(file);
  if (!result.isValid) {
    throw new Error(result.error || "CSV validation failed");
  }
  return result.parsedData || [];
};

/**
 * Parses a CSV file and returns detailed parsing results
 * @param file - The CSV file to parse
 * @returns Promise<CSVParseResult> - Detailed parsing results with headers, data, and index mapping
 */
export const parseCSVFileDetailed = async (file: File): Promise<CSVParseResult> => {
  const result = await validateCSVShapeDetailed(file);
  if (!result.isValid) {
    throw new Error(result.error || "CSV validation failed");
  }

  return {
    headers: result.headers || [],
    data: result.parsedData || [],
    headerIndexMapping: [], // This would need to be calculated in the validation function if needed
  };
};

/**
 * Check if a column contains only integer values
 * @param data - Array of parsed CSV data objects
 * @param header - Column header to check
 * @returns boolean - True if all non-empty values in the column are integers
 */
export const columnIsInteger = (data: Record<string, string>[], header: string): boolean => {
  if (!data || data.length === 0) return false;
  
  for (const row of data) {
    const value = row[header];
    if (value && value.trim() !== "") {
      const trimmed = value.trim();
      const parsed = parseInt(trimmed, 10);
      if (isNaN(parsed) || parsed.toString() !== trimmed) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Check if a column has unique non-empty values
 * @param data - Array of parsed CSV data objects
 * @param header - Column header to check
 * @returns boolean - True if all non-empty values in the column are unique AND every row has a value
 */
export const columnHasUniqueValues = (data: Record<string, string>[], header: string): boolean => {
  if (!data || data.length === 0) return false;

  const seenValues = new Set<string>();
  let rowsWithValues = 0;

  for (const row of data) {
    const value = row[header];
    if (value && value.trim() !== "") {
      const trimmed = value.trim();
      if (seenValues.has(trimmed)) {
        return false;
      }
      seenValues.add(trimmed);
      rowsWithValues++;
    } else {
      // If any row is missing a value for this column, it's not suitable for Plot ID
      return false;
    }
  }

  // Must have values for all rows and all values must be unique
  return rowsWithValues === data.length && seenValues.size === rowsWithValues;
};

/**
 * Check if a column is entirely empty (every data row blank or whitespace-only).
 * Returns false for empty data — with no rows there is nothing to judge as empty,
 * matching the other column-analysis helpers.
 * @param data - Array of parsed CSV data objects
 * @param header - Column header to check
 * @returns boolean - True if every row's value for the column is blank
 */
export const columnIsEmpty = (data: Record<string, string>[], header: string): boolean => {
  if (!data || data.length === 0) return false;
  return data.every((row) => {
    const value = row[header];
    return !value || value.trim() === "";
  });
};

/**
 * Check if a pair of columns creates unique identifiers
 * @param data - Array of parsed CSV data objects
 * @param headerA - First column header
 * @param headerB - Second column header
 * @returns boolean - True if the combination of the two columns creates unique pairs
 */
export const columnPairCreatesUniqueIdentifiers = (
  data: Record<string, string>[], 
  headerA: string, 
  headerB: string
): boolean => {
  if (!data || data.length === 0) return false;
  
  const seenPairs = new Set<string>();
  let validPairCount = 0;
  
  for (const row of data) {
    const valueA = row[headerA];
    const valueB = row[headerB];
    
    if (valueA && valueA.trim() !== "" && valueB && valueB.trim() !== "") {
      const trimmedA = valueA.trim();
      const trimmedB = valueB.trim();
      const pairKey = `${trimmedA}|${trimmedB}`;
      
      if (seenPairs.has(pairKey)) {
        return false;
      }
      seenPairs.add(pairKey);
      validPairCount++;
    }
  }
  
  // All rows must have valid pairs and they must all be unique
  return validPairCount === data.length && seenPairs.size === data.length;
};

/**
 * Calculate number of rows from CSV data
 * @param data - Array of parsed CSV data objects
 * @param rowColumn - Column header to use for row calculation
 * @returns number - Number of unique rows, or total data length for single column layout
 */
export const calculateRowsFromData = (data: Record<string, string>[], rowColumn?: string): number => {
  if (!data || data.length === 0) return 0;
  if (!rowColumn) return data.length; // Single column layout
  
  const uniqueRows = new Set<string>();
  data.forEach(row => {
    const value = row[rowColumn];
    if (value && value.trim() !== "") {
      uniqueRows.add(value.trim());
    }
  });
  return uniqueRows.size;
};

/**
 * Calculate number of columns from CSV data
 * @param data - Array of parsed CSV data objects
 * @param colColumn - Column header to use for column calculation
 * @returns number - Number of unique columns, or 1 for single column layout
 */
export const calculateColsFromData = (data: Record<string, string>[], colColumn?: string): number => {
  if (!data || data.length === 0) return 0;
  if (!colColumn) return 1; // Single column layout
  
  const uniqueCols = new Set<string>();
  data.forEach(row => {
    const value = row[colColumn];
    if (value && value.trim() !== "") {
      uniqueCols.add(value.trim());
    }
  });
  return uniqueCols.size;
};

/**
 * Analyze CSV columns for plot map requirements
 * @param data - Array of parsed CSV data objects
 * @param headers - Array of column headers
 * @returns CSVColumnAnalysis - Analysis results for column selection
 */
export const analyzeCSVColumns = (data: Record<string, string>[], headers: string[]): CSVColumnAnalysis => {
  const startTime = performance.now();
  console.log(`[CSVAnalysis] Starting analysis of ${data.length} rows with ${headers.length} columns`);

  const uniqueValueColumns: string[] = [];
  const integerColumns: string[] = [];
  const emptyColumns: string[] = [];
  const rowColumnCandidates: Record<string, string[]> = {};

  // Find columns with unique values, integer columns, and entirely-empty columns
  const uniqueStartTime = performance.now();
  for (const header of headers) {
    if (columnHasUniqueValues(data, header)) {
      uniqueValueColumns.push(header);
    }
    if (columnIsInteger(data, header)) {
      integerColumns.push(header);
    }
    if (columnIsEmpty(data, header)) {
      emptyColumns.push(header);
    }
  }
  console.log(`[CSVAnalysis] Unique/integer check completed in ${(performance.now() - uniqueStartTime).toFixed(0)}ms`);

  // Find valid row/column combinations
  // Only consider pairs where NEITHER column alone has unique values
  // (since we want row/column combinations, not already-unique identifiers)
  // OPTIMIZATION: Skip this expensive O(n*m^2) operation for very large datasets
  const pairStartTime = performance.now();
  if (data.length <= 10000) {
    for (const headerA of headers) {
      const candidates: string[] = [];
      for (const headerB of headers) {
        if (headerA !== headerB &&
            columnPairCreatesUniqueIdentifiers(data, headerA, headerB) &&
            !uniqueValueColumns.includes(headerA) &&
            !uniqueValueColumns.includes(headerB)) {
          candidates.push(headerB);
        }
      }
      if (candidates.length > 0) {
        rowColumnCandidates[headerA] = candidates;
      }
    }
    console.log(`[CSVAnalysis] Pair analysis completed in ${(performance.now() - pairStartTime).toFixed(0)}ms`);
  } else {
    console.log(`[CSVAnalysis] Skipping pair analysis for large dataset (${data.length} rows > 10000)`);
  }

  const totalTime = performance.now() - startTime;
  console.log(`[CSVAnalysis] Complete in ${totalTime.toFixed(0)}ms - Found ${uniqueValueColumns.length} unique cols, ${integerColumns.length} int cols`);

  return {
    uniqueValueColumns,
    integerColumns,
    rowColumnCandidates,
    emptyColumns,
  };
};

/**
 * From a CSV validation result, return the entirely-empty columns that are
 * METADATA columns — i.e. excluding the user-selected structural columns
 * (Plot ID / Row / Column). These are the columns offered for dropping on upload.
 * @param result - CSV validation result (may be undefined / pre-analysis)
 * @param structural - The currently selected structural column names
 * @returns string[] - Empty metadata column names (order follows the analysis)
 */
export const getEmptyMetadataColumns = (
  result: CSVValidationResult | undefined,
  structural: { plotIdColumn?: string; rowColumn?: string; colColumn?: string }
): string[] => {
  const emptyColumns = result?.columnAnalysis?.emptyColumns;
  if (!emptyColumns || emptyColumns.length === 0) return [];
  const structuralNames = new Set(
    [structural.plotIdColumn, structural.rowColumn, structural.colColumn].filter(Boolean)
  );
  return emptyColumns.filter((column) => !structuralNames.has(column));
};

/**
 * Return `headers` with the given columns removed, preserving order. Used to
 * drop empty metadata columns from an experiment's stored column schema.
 * @param headers - Full ordered header list
 * @param columnsToDrop - Column names to remove
 * @returns string[] - Headers excluding the dropped columns
 */
export const dropColumnsFromHeaders = (headers: string[], columnsToDrop: string[]): string[] => {
  if (columnsToDrop.length === 0) return headers;
  const dropSet = new Set(columnsToDrop);
  return headers.filter((header) => !dropSet.has(header));
};
